<script lang="ts">
	import bioBackground from "@assets/images/banner/background.png?format=webp&imagetools";
	import type { EmblaCarouselType, EmblaPluginType } from "embla-carousel";
	import autoplay from "embla-carousel-autoplay";
	import type { ItemList, Person } from "schema-dts";
	import { getAboutUsPath } from "@core/schema/paths/getAboutUsPath.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { homepageData } from "../../data/homepageData.js";
	import { homepageImages } from "../../data/homepageImages.js";
	import Carousel from "../common/Carousel.svelte";
	import Formatted from "../common/Formatted.svelte";
	import Link from "../common/Link.svelte";
	import Media from "../common/Media.svelte";
	import StructuredData from "../common/StructuredData.svelte";
	import { ButtonVariant } from "../common/getButtonClass.js";

	const { tenant } = getGlobalTemplateProps();

	const bioPersons = homepageData.bioPersons.map((item) => {
		const photoKey = item.photoKey as keyof typeof homepageImages;
		const img = homepageImages[photoKey];
		const photo = typeof img === "string" ? img : img.desktop;
		return {
			name: item.name,
			photo,
			photoAlt: item.photoAlt,
			text: item.text,
		};
	});

	const employeesData: ItemList = {
		"@type": "ItemList",
		itemListElement: homepageData.bioPersons.map((person, index) => ({
			"@type": "ListItem",
			position: index + 1,
			item: {
				"@type": "Person",
				name: person.name,
				jobTitle: "Head Spa Specialista",
				description: person.text,
				workLocation: {
					"@type": "Place",
					name: "Head Spa Napohodu",
					address: {
						"@type": "PostalAddress",
						streetAddress: "Hrnčířská 42/5",
						addressLocality: "Karviná",
						postalCode: "733 01",
						addressCountry: "CZ",
					},
				},
			} satisfies Person,
		})),
	};

	const autoplayPlugin: EmblaPluginType = autoplay({ delay: 5000, stopOnInteraction: false });
	let carouselApi = $state<EmblaCarouselType | undefined>(undefined);
	let selectedIndex = $state(0);

	$effect(() => {
		if (!carouselApi) {
			return;
		}

		const api = carouselApi;
		const onSelect = (): void => {
			selectedIndex = api.selectedScrollSnap();
		};

		api.on("select", onSelect);
		onSelect();

		return (): void => {
			api.off("select", onSelect);
		};
	});
</script>

<StructuredData data={employeesData} />

<section class="grid-container !py-4 md:!py-8">
	<div class="flex flex-col items-start full-width">
		<div
			class="flex flex-col items-center py-8 md:py-12 lg:py-16 px-4 md:px-8 lg:px-0 gap-6 md:gap-8 w-full rounded-[2rem] md:rounded-[4rem] lg:rounded-[6rem] xl:rounded-[8rem] overflow-hidden bg-cover bg-center relative"
			style="background-image: url({bioBackground})"
		>
			<div class="relative z-10 flex flex-col items-center gap-6 md:gap-8 w-full">
				<h2 class="text-ivory-100">
					<span class="inline-block">
						<Formatted text="Kdo bude pečovat o vaši" />
						<span class="relative inline-block">
							pohodu
							<span class="absolute bottom-0 left-0 h-1 w-full bg-current"></span>
						</span>
					</span>
				</h2>

				<Carousel
					bind:api={carouselApi}
					items={bioPersons}
					options={{ loop: true, align: "center" }}
					plugins={[autoplayPlugin]}
					class="w-full"
					itemsContainerClass="flex"
				>
					{#snippet item(person: (typeof bioPersons)[0])}
						{@const { photo, photoAlt, text } = person as {
							photo: string;
							photoAlt: string;
							text: string;
						}}
						<div class="flex flex-col items-center gap-6 md:gap-8 flex-shrink-0 w-full">
							<div
								class="w-48 h-48 md:w-56 md:h-56 xl:w-64 xl:h-64 [@media(max-height:700px)]:w-40 [@media(max-height:700px)]:h-40 [@media(max-height:700px)]:md:w-48 [@media(max-height:700px)]:md:h-48 [@media(max-height:700px)]:xl:w-52 [@media(max-height:700px)]:xl:h-52 rounded-[12rem] md:rounded-[16rem] xl:rounded-[21.25rem] overflow-hidden"
							>
								<Media
									source={{ src: String(photo), type: "image" }}
									sourcesets={{}}
									alt={photoAlt}
									width={256}
									height={256}
									class="w-full h-full object-cover"
								/>
							</div>

							<p
								class="font-medium text-text-mobile md:text-p text-center text-ivory-100 max-w-[20rem] md:max-w-[35rem] xl:max-w-[46.25rem] px-4 md:px-0"
							>
								<Formatted {text} />
							</p>
						</div>
					{/snippet}
				</Carousel>

				<div class="flex gap-2 items-center justify-center">
					{#each bioPersons as _, index (index)}
						<button
							type="button"
							class="w-2 h-2 rounded-full transition-all {selectedIndex === index
								? 'bg-ivory-100 w-6 md:w-8'
								: 'bg-ivory-100/50'}"
							onclick={(): void => {
								carouselApi?.scrollTo(index);
							}}
							aria-label={`Přejít na slide ${index + 1}`}
						></button>
					{/each}
				</div>

				<Link variant={ButtonVariant.SecondaryMedium} href={getAboutUsPath(tenant.locale)}>Dozvědět se více o nás</Link>
			</div>
		</div>
	</div>
</section>
